import React from 'react';
import { LEVELS } from '../hooks/useRepairCycle';

/*
 * What the damage looks like, at each of the five stages.
 *
 * Authored against the left edge with the panel to the right, so cracks run
 * from x=0 inward; a right-hand edge mirrors the whole thing. Stages are
 * classes over shared geometry rather than separate drawings: the same cracks
 * are revealed further, widened and darkened as it worsens, which is what makes
 * the repair read as this damage healing rather than one picture swapped for
 * another.
 *
 * Chips are filled with the page's own ground, so they read as material gone
 * from the panel rather than as marks drawn on top of it.
 */

export const VB_W = 30;
export const VB_H = 132;

const CRACKS = [
  { d: 'M 0 14 L 7 19 L 13 16 L 20 23 L 27 20', from: 1 },
  { d: 'M 0 62 L 8 56 L 14 64 L 22 59 L 28 66', from: 1 },
  { d: 'M 0 94 L 6 101 L 14 95 L 21 103 L 29 98', from: 2 },
  { d: 'M 0 40 L 9 45 L 15 39 L 23 46', from: 2 },
  { d: 'M 0 118 L 7 112 L 15 119 L 24 113', from: 2 },
  { d: 'M 0 78 L 6 83 L 12 77 L 19 84 L 26 79', from: 3 },
  { d: 'M 0 28 L 8 24 L 14 30 L 21 26', from: 3 },
  { d: 'M 0 106 L 7 110 L 13 104 L 20 111', from: 4 },
  // Branches, which only open once the cracks are deep.
  { d: 'M 13 16 L 16 7 L 22 4', from: 3 },
  { d: 'M 14 64 L 17 73 L 24 77', from: 3 },
  { d: 'M 15 39 L 13 31 L 18 26', from: 3 },
  { d: 'M 14 95 L 12 87 L 17 82', from: 4 },
  { d: 'M 12 77 L 9 70 L 3 68', from: 4 },
  { d: 'M 15 119 L 19 126 L 26 124', from: 4 },
];

const CHIPS = [
  { d: 'M 0 24 L 8 29 L 5 37 L 0 36 Z', from: 3 },
  { d: 'M 0 70 L 7 75 L 4 83 L 0 82 Z', from: 3 },
  { d: 'M 0 100 L 9 105 L 6 113 L 0 111 Z', from: 4 },
  { d: 'M 0 2 L 7 6 L 4 13 L 0 12 Z', from: 4 },
  { d: 'M 0 46 L 6 50 L 3 57 L 0 56 Z', from: 4 },
];

/* Where the fault arcs: crack ends, so the sparks come off the break itself. */
const ARCS = [
  { cx: 27, cy: 20, delay: 0 },
  { cx: 28, cy: 66, delay: 0.5 },
  { cx: 29, cy: 98, delay: 0.9 },
  { cx: 23, cy: 46, delay: 1.4 },
  { cx: 24, cy: 113, delay: 1.8 },
  { cx: 26, cy: 79, delay: 2.3 },
  { cx: 22, cy: 4, delay: 2.7 },
  { cx: 24, cy: 77, delay: 3.2 },
];

/* Débris off the break, falling clear of the panel. */
const FALLS = [
  { cx: 6, cy: 22, dur: 2.4, delay: 0 },
  { cx: 12, cy: 60, dur: 3.1, delay: 0.7 },
  { cx: 4, cy: 92, dur: 2.7, delay: 1.3 },
  { cx: 15, cy: 38, dur: 3.4, delay: 1.9 },
  { cx: 9, cy: 110, dur: 2.6, delay: 2.4 },
  { cx: 18, cy: 74, dur: 3.0, delay: 3.0 },
];

/*
 * The panel giving way, in six places rather than one.
 *
 * Each seat throws a cluster of balls; the clusters are generated once from a
 * fixed seed so they are varied but never reshuffle between renders. Colour
 * runs outward from white-hot through amber to deep orange, and the biggest
 * balls take the coolest tone, which is the way a real fireball reads.
 */
/*
 * An LCG's first output barely moves between nearby seeds -- seeding six
 * clusters this way gave all six an identical ball count. Burning a few values
 * first spreads them, which is the only reason the clusters differ at all.
 */
const seeded = (seed) => {
  let value = seed;
  const next = () => {
    value = (value * 1664525 + 1013904223) % 4294967296;
    return value / 4294967296;
  };
  for (let i = 0; i < 6; i += 1) next();
  return next;
};

const BLAST_SEATS = [
  { cx: 4, cy: 16, delay: 0 },
  { cx: 10, cy: 58, delay: 0.42 },
  { cx: 5, cy: 96, delay: 0.84 },
  { cx: 14, cy: 36, delay: 1.26 },
  { cx: 7, cy: 120, delay: 1.68 },
  { cx: 12, cy: 78, delay: 2.1 },
];

const BLASTS = BLAST_SEATS.map((seat, index) => {
  const random = seeded(101 + index * 37);
  // Three to ten balls a seat, so no two blasts are the same size or shape.
  const count = 3 + Math.floor(random() * 8);
  return {
    ...seat,
    balls: Array.from({ length: count }, (_, i) => {
      const scale = 0.45 + random() * 0.55;
      return {
        dx: (random() - 0.5) * 11,
        dy: (random() - 0.5) * 13,
        r: 2 + scale * 5,
        // Big and cool on the outside, small and white at the core.
        tone: scale > 0.75 ? 'outer' : scale > 0.5 ? 'mid' : 'core',
        delay: seat.delay + i * 0.05,
      };
    }),
  };
});

const PanelDamage = ({ level, side }) => {
  const stage = LEVELS[level];
  const mirror = side !== 'left';

  return (
    <svg
      className={`dmg is-${side} is-${stage}`}
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      preserveAspectRatio="none"
      focusable="false"
      aria-hidden="true"
    >
      <g transform={mirror ? `translate(${VB_W} 0) scale(-1 1)` : undefined}>
        {CHIPS.map((chip, i) => (
          <path key={`k${i}`} className={`dmg-chip from-${chip.from}`} d={chip.d} />
        ))}
        {CRACKS.map((crack, i) => (
          <path key={`c${i}`} className={`dmg-crack from-${crack.from}`} d={crack.d} />
        ))}

        {FALLS.map((fall, i) => (
          <circle
            key={`f${i}`}
            className="dmg-fall"
            cx={fall.cx}
            cy={fall.cy}
            r={1.1}
            style={{ animationDuration: `${fall.dur}s`, animationDelay: `${fall.delay}s` }}
          />
        ))}

        {ARCS.map((arc, i) => (
          <circle
            key={`a${i}`}
            className="dmg-arc"
            cx={arc.cx}
            cy={arc.cy}
            r={1.5}
            style={{ animationDelay: `${arc.delay}s` }}
          />
        ))}

        {BLASTS.map((blast, i) => (
          <g key={`b${i}`}>
            {blast.balls.map((ball, j) => (
              <circle
                key={j}
                className={`dmg-ball is-${ball.tone}`}
                cx={blast.cx + ball.dx}
                cy={blast.cy + ball.dy}
                r={ball.r}
                style={{ animationDelay: `${ball.delay}s` }}
              />
            ))}
          </g>
        ))}
      </g>
    </svg>
  );
};

export default PanelDamage;
